import { HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { WarehouseRepository } from '@asm/features/sync/repositories/warehouse.repository';
import { DeliveryRouteRepository } from '@asm/features/sync/repositories/delivery-route.repository';

/**
 * Handler offline para warehouses e rotas de entrega.
 *
 * Cobre:
 * - GET /warehouse/select
 * - GET /delivery-routes/available-days?b2bUnitCode=&warehouseCode=
 */
@Injectable({ providedIn: 'root' })
export class WarehouseOfflineHandler {
  private readonly warehouseRepo = inject(WarehouseRepository);
  private readonly deliveryRouteRepo = inject(DeliveryRouteRepository);

  canHandle(req: HttpRequest<unknown>): boolean {
    return (
      req.method === 'GET' &&
      (req.url.includes('/warehouse/select') || req.url.includes('/delivery-routes/available-days'))
    );
  }

  async handle(req: HttpRequest<unknown>): Promise<HttpResponse<unknown>> {
    const { url } = req;

    if (url.includes('/warehouse/select')) {
      const distributionChannel = req.params.get('distributionChannel') ?? null;
      let warehouses = await firstValueFrom(this.warehouseRepo.getAll());

      if (distributionChannel) {
        warehouses = warehouses.filter(
          (w: any) => String(w['distributionChannelCode'] ?? '').trim() === distributionChannel.trim(),
        );
      }

      return new HttpResponse({ status: 200, body: warehouses });
    }

    if (url.includes('/delivery-routes/available-days')) {
      const b2bUnitCode = req.params.get('b2bUnitCode') ?? '';
      const warehouseCode = req.params.get('warehouseCode') ?? '';
      return new HttpResponse({ status: 200, body: await this.getAvailableWeekDays(b2bUnitCode, warehouseCode) });
    }

    throw new Error(`[WarehouseOfflineHandler] Rota não mapeada: ${req.method} ${url}`);
  }

  /**
   * Retorna os índices de dias da semana (0=Dom … 6=Sáb) disponíveis para entrega
   * na combinação b2bUnit + armazém.
   */
  async getAvailableWeekDays(b2bUnitCode: string, warehouseCode: string): Promise<number[]> {
    if (!b2bUnitCode || !warehouseCode) return [];

    const routes = await firstValueFrom(this.deliveryRouteRepo.getAll());
    const route = routes.find(r => r.b2bUnitCode === b2bUnitCode && r.warehouseCode === warehouseCode);
    if (!route) return [];

    const flags = [
      route.sunday,
      route.monday,
      route.tuesday,
      route.wednesday,
      route.thursday,
      route.friday,
      route.saturday,
    ];

    return flags.flatMap((enabled, index) => (enabled ? [index] : []));
  }
}
